import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';

import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { tap } from 'rxjs/operators';
import { APP_CONFIG, REST_BASE_URL } from '../app.config';
import { AppConfig } from '../models/config/app-config';
import { ValueList, ValueListID, ValueListItem } from '../models/rest/valuelist/valuelist';
import { LanguageService } from './language.service';

const KEY_PENULTIMATE_BERUF = '30';
const KEY_LAST_BERUF = '99';

const VALUELIST_NAMES = [
  'anzahleinreisen',
  'aufenthaltszweck',
  'befoerderungsmittel',
  'begruendung_fehlende_referenz',
  'behoerde',
  'beruf',
  'd_visum_aufenthaltszweck',
  'dokument_art',
  'einreisegenehmigung_art',
  'familienstand',
  'fingerabdruecke_ausnahmegrund',
  'geschlecht',
  'land',
  'organisation_register',
  'organisation_wirkungsbereich',
  'pass_art',
  'referenz_art',
  'schengenlaendercode',
  'schengenland',
  'verpflichtungserklaerungsgeber_art',
  'verwandtschaftsbeziehung',
  'visum_kategorie'
];

// Vergleichsfunktionen für die zu sortierenden ValueLists (Wie bei einem Comparator in Java
// müssen die Funktionen eine number <0, ==0 oder >0 liefern, falls a<b, a==b bzw. a>b.)
// Beispiele:
//  "land": (a, b) => b.key.localeCompare(a.key),       // "land" absteigend nach Schlüsselwert
//  "land": (a, b) => a.value.length - b.value.length,  // "land" aufsteigend nach Länge des Anzeigewerte
//  "pass_art": (a, b) => +a.key - +b.key,              // "pass_art" aufsteigend nach nummerischem Schlüsselwert
//  "behoerde": (a, b) => {                             // "behoerde" aufsteigend - falls vorhanden, nummerisch nach
//    const a_match = a.value.match(/^\d+/);            //   der im Anzeigewert enthaltenen PLZ bzw. Behördenkennziffer,
//    const b_match = b.value.match(/^\d+/);            //   andernfalls alphabetisch nach komplettem Anzeigewert
//    return a_match && b_match
//      ? +a_match[0] - +b_match[0]
//      : a.value.localeCompare(b.value)
//  }
const VALUELIST_SORT_FN: {
  [listName: string]: (a: ValueListItem, b: ValueListItem) => number
} = {
  'land': (a, b) => a.value.localeCompare(b.value),           // "land" aufsteigend nach Anzeigewert
  'behoerde': (a, b) => extractAvNumber(a.value) - extractAvNumber(b.value),
  'dokument_art': (a, b) => a.value.localeCompare(b.value),
  'schengenland': (a, b) => a.value.localeCompare(b.value),
  'fingerabdruecke_ausnahmegrund': (a, b) => a.value.localeCompare(b.value),
  'beruf': (a, b) => {

    if (KEY_LAST_BERUF === a.key) {
      return 1;
    } else if (KEY_LAST_BERUF === b.key) {
      return -1;
    }
    if (KEY_PENULTIMATE_BERUF === a.key) {
      return 1;
    } else if (KEY_PENULTIMATE_BERUF === b.key) {
      return -1;
    }
    if (KEY_LAST_BERUF === a.key && KEY_PENULTIMATE_BERUF === b.key) {
      return 1;
    } else if (KEY_PENULTIMATE_BERUF === a.key && KEY_LAST_BERUF === b.key) {
      return -1;
    }
    return a.value.localeCompare(b.value);
  }
};

function extractAvNumber(inputString) {
  const match = inputString.match(/^\d+/);
  return match ? parseInt(match[0], 10) : -1;
}

@Injectable()
export class ValueListsService {
  private listIds: {
    [key: string]: ValueListID
  } = {};

  private listItems: {
    [key: string]: BehaviorSubject<ValueListItem[]>
  } = {};

  constructor(
    private http: HttpClient,
    private languageService: LanguageService,
    @Inject(APP_CONFIG) private appConfig: AppConfig
  ) {
  }

  fetchLists(): Observable<ValueList[]> {
    const ret = new Subject<ValueList[]>();
    const serviceUrl = REST_BASE_URL + '/visum-antrag/valuelists/';
    const params = new HttpParams()
    .set('visumAntragsartEnum', this.appConfig.visumAntragsartEnum)
    .set('language', this.languageService.getLanguage());

    this.http
    .get<ValueList[]>(serviceUrl, { params }).pipe(
      tap((result: ValueList[]) => {
        result.forEach((valueList: ValueList) => {
          const listName: string = valueList.valueListID.name;
          if (VALUELIST_NAMES.indexOf(listName) >= 0) {
            this.listIds[listName] = valueList.valueListID;
            this.preProcessValueList(valueList);
            this.sortItems(valueList);
            this.handleTaiwanIssue(valueList);
            this.setValueListItems(listName, valueList.valueListItems);
            this.postProcessValueList(valueList);
          }
        });
      }))
    .subscribe({
        next:
          (valueLists: ValueList[]) => {
            ret.next(valueLists);
          },
        error: error => {
          console.error('ERROR: ' + serviceUrl, params, error.status);
          ret.next([]);
        }
      }
    );
    return ret;
  }

  preProcessValueList(valueList: ValueList) {
    switch (valueList.valueListID.name) {
      case 'fingerabdruecke_ausnahmegrund':
        valueList.valueListItems = valueList.valueListItems.map(value => this.parseMultivalueItem(value, 'bezeichnung'));
        return;
      case 'land':
        valueList.valueListItems = valueList.valueListItems.map(value => this.parseMultivalueItem(value, 'bezeichnung'));
        return;
      case 'dokument_art':
        valueList.valueListItems = valueList.valueListItems.map(value => this.parseMultivalueItem(value, 'dokumentenname'));
        return;
    }
  }

  postProcessValueList(valueList: ValueList) {
    switch (valueList.valueListID.name) {
      case 'land':
        this.cloneValueList(valueList, 'land', item => {
          return !item.fields || item.fields.laender;
        });
        this.cloneValueList(valueList, 'staatsangehoerigkeit', item => {
          return !item.fields || item.fields.staatsangehoerigkeiten;
        });
        this.cloneValueList(valueList, 'passaustellenderstaat', item => {
          return !item.fields || item.fields.passaustellenderstaat;
        });
        return;
    }
  }

  cloneValueList(source: ValueList, newName: string, filterFn: (item: ValueListItem) => boolean) {
    const targetId: ValueListID = {
      name: newName,
      context: source.valueListID.context,
      language: source.valueListID.language,
      version: source.valueListID.version
    }

    const newItems = source.valueListItems.filter(filterFn);
    this.listIds[newName] = targetId;
    this.setValueListItems(newName, newItems);
  }

  private setValueListItems(listName: string, valueListItems: ValueListItem[]) {
    if (!this.listItems[listName]) {
      // New ValueList => Add new BehaviorSubject for this ValueList which can be subscribed by observers
      this.listItems[listName] = new BehaviorSubject<ValueListItem[]>(valueListItems);
    } else {
      // ValueList changed => Emitting new Value to observers by calling next() on the BehaviorSubject.
      this.listItems[listName].next(valueListItems);
    }
  }

  getValueListId(listName: string): ValueListID {
    // console.debug('getValueListId(' + listName + ') = ', this.listIds[listName]);
    return this.listIds[listName];
  }

  getValueListItems(listName: string): Observable<ValueListItem[]> {
    // console.debug('getValueListItems(' + listName + ') = ', this.listItems[listName]);

    const valueListItems: Observable<ValueListItem[]> = this.listItems[listName];
    if (valueListItems) {
      return valueListItems;
    } else {
      console.error('Werteliste konnte nicht geladen werden: ' + listName);
      return new BehaviorSubject<ValueListItem[]>(null);
    }
  }

  private sortItems(valuelist: ValueList): void {
    if (VALUELIST_SORT_FN[valuelist.valueListID.name]) {
      valuelist.valueListItems.sort(VALUELIST_SORT_FN[valuelist.valueListID.name]);
    }
  }

  private handleTaiwanIssue(valuelist: ValueList): void {
    if (valuelist.valueListID.name === 'land') {
      valuelist.valueListItems
      .filter(item => item.key === 'TWN')
      .forEach(item => item.value += ' **');
    }
  }

  public parseMultivalueItem(value: ValueListItem, valueFieldName: string): ValueListItem {
    let fields;
    let parsedValue;
    try {
      fields = JSON.parse(value.value);
      parsedValue = fields[valueFieldName] || value.value;
    } catch (e) {
      parsedValue = value.value; // Not a JSON, return original value
    }

    return {
      key: value.key,
      value: parsedValue,
      fields: fields
    };
  }

}
